const DEFAULT_FINISH_MS = 900;

function getRevealSpeed(backlogLength) {
  if (backlogLength > 1000) return 420;
  if (backlogLength > 400) return 260;
  if (backlogLength > 120) return 140;
  return 55;
}

export function createProgressiveTextReveal({
  onReveal,
  isActive = () => true,
  finishMs = DEFAULT_FINISH_MS,
} = {}) {
  let targetText = '';
  let visibleText = '';
  let frameId = null;
  let lastRevealTime = null;
  let revealDeadline = null;
  let resolveFinish = null;

  const resolveIfComplete = () => {
    if (resolveFinish && visibleText.length >= targetText.length) {
      const resolve = resolveFinish;
      resolveFinish = null;
      resolve();
    }
  };

  const cancelFrame = () => {
    if (frameId) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
  };

  const cancel = () => {
    cancelFrame();
    lastRevealTime = null;
    revealDeadline = null;
    if (resolveFinish) {
      const resolve = resolveFinish;
      resolveFinish = null;
      resolve();
    }
  };

  const show = (content) => {
    if (isActive() && onReveal) {
      onReveal(content);
    }
  };

  const schedule = () => {
    if (!isActive()) {
      cancel();
      return;
    }
    if (!frameId) {
      frameId = requestAnimationFrame(revealNextFrame);
    }
  };

  const revealNextFrame = (now) => {
    frameId = null;
    if (!isActive()) {
      cancel();
      return;
    }

    const backlogLength = targetText.length - visibleText.length;
    if (backlogLength <= 0) {
      lastRevealTime = null;
      revealDeadline = null;
      resolveIfComplete();
      return;
    }

    const elapsedSeconds = lastRevealTime
      ? Math.max((now - lastRevealTime) / 1000, 0.016)
      : 0.016;
    lastRevealTime = now;

    let charsToReveal = Math.max(
      1,
      Math.floor(getRevealSpeed(backlogLength) * elapsedSeconds),
    );
    if (revealDeadline) {
      const framesLeft = Math.max(1, Math.ceil((revealDeadline - now) / 16));
      charsToReveal = Math.max(
        charsToReveal,
        Math.ceil(backlogLength / framesLeft),
      );
    }

    visibleText = targetText.slice(0, visibleText.length + charsToReveal);
    show(visibleText);

    if (visibleText.length < targetText.length) {
      schedule();
    } else {
      lastRevealTime = null;
      revealDeadline = null;
      resolveIfComplete();
    }
  };

  const queue = (content) => {
    targetText = content || '';
    if (!targetText.startsWith(visibleText)) {
      visibleText = targetText;
      cancel();
      show(visibleText);
      return Promise.resolve();
    }
    schedule();
    return undefined;
  };

  const finish = (content) => {
    if (!isActive()) {
      return Promise.resolve();
    }
    queue(content);
    if (visibleText.length >= targetText.length) {
      return Promise.resolve();
    }
    revealDeadline = performance.now() + finishMs;
    return new Promise((resolve) => {
      resolveFinish = resolve;
      schedule();
    });
  };

  return {
    queue,
    finish,
    cancel,
  };
}
